import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CalendarDots, CreditCard, CheckCircle, ArrowLeft, Phone, Copy, UploadSimple, Sparkle } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import LanguageToggle from '../components/LanguageToggle';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo-tk.png";
const ZELLE_PHONE = "5618596830";

export default function DropInClass() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isSuccess = searchParams.get('success') === 'true';

  const [step, setStep] = useState(isSuccess ? 'success' : 'form');
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', preferred_date: '', notes: '' });
  const [isFirstTrial, setIsFirstTrial] = useState(false);
  const [eligible, setEligible] = useState(true);
  const [checkingEligible, setCheckingEligible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [regId, setRegId] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  // Confirma pagamento por cartao ao voltar do Stripe (independe do webhook)
  useEffect(() => {
    const sid = searchParams.get('session_id');
    if (!isSuccess || !sid) return;
    let tries = 0;
    const confirm = async () => {
      tries += 1;
      try {
        const { data } = await axios.get(`${API}/drop-in/checkout-status/${sid}`);
        if (data.payment_status !== 'paid' && tries < 4) setTimeout(confirm, 1500);
      } catch { if (tries < 4) setTimeout(confirm, 1500); }
    };
    confirm();
  }, [isSuccess, searchParams]);

  // Re-check eligibility when phone/email changes (debounced)
  useEffect(() => {
    const phone = (formData.phone || '').replace(/\D/g, '');
    const email = (formData.email || '').trim();
    if (phone.length < 7 && !email) {
      setEligible(true);
      return;
    }
    const t = setTimeout(async () => {
      setCheckingEligible(true);
      try {
        const params = new URLSearchParams();
        if (phone) params.set('phone', phone);
        if (email) params.set('email', email);
        const { data } = await axios.get(`${API}/first-trial/eligible?${params.toString()}`);
        setEligible(!!data.eligible);
        if (!data.eligible) setIsFirstTrial(false);
      } catch {
        setEligible(true);
      } finally {
        setCheckingEligible(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [formData.phone, formData.email]);

  const amount = isFirstTrial ? 15 : 25;
  const amountCard = (amount * 1.05).toFixed(2);

  const handleSubmitForm = (e) => {
    e.preventDefault();
    setStep('payment');
  };

  const handlePayWithCard = async () => {
    setLoading(true);
    try {
      const { data: regData } = await axios.post(`${API}/drop-in/register`, {
        ...formData,
        is_first_trial: isFirstTrial,
      });
      const originUrl = window.location.origin;
      const { data: checkoutData } = await axios.post(`${API}/drop-in/checkout`, {
        registration_id: regData.id,
        origin_url: originUrl
      });
      if (checkoutData.url) {
        setCheckoutUrl(checkoutData.url);
        setStep('redirecting');
        // Give React 800ms to render the fallback screen BEFORE we redirect.
        // In webviews (Instagram/Facebook/TikTok) that block third-party redirects,
        // the fallback button stays visible so the user can tap it manually.
        setTimeout(() => { window.location.assign(checkoutData.url); }, 800);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao processar');
      setLoading(false);
    }
  };

  const handlePayWithZelle = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/drop-in/register`, {
        ...formData,
        is_first_trial: isFirstTrial,
        notes: (formData.notes ? formData.notes + ' | ' : '') + 'Pagamento via Zelle'
      });
      setRegId(data.id);
      setStep('zelle-info');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    } finally {
      setLoading(false);
    }
  };

  const handleReceiptUpload = async (file) => {
    if (!file || !regId) return;
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/drop-in/zelle-receipt?registration_id=${regId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Comprovante enviado! Pagamento confirmado.');
      setStep('success');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar comprovante');
    } finally {
      setUploadingReceipt(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-white border-b border-[#E6E2D8] px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-tk.png" alt="Logo" className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display='none'; }} />
            <span className="font-['Outfit'] font-bold text-[#1A1A1A]">Tia Karol Sports</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login" className="text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium">
              {t('common.doLogin')}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#73706A] hover:text-[#1A1A1A] mb-6">
          <ArrowLeft size={16} /> {t('common.back')}
        </Link>

        {/* Success */}
        {step === 'success' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center">
            <div className="w-20 h-20 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-white" weight="fill" />
            </div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] mb-3">{t('dropIn.confirmedTitle')}</h2>
            <p className="text-[#73706A] mb-6">{t('dropIn.confirmedSubtitle')}</p>
            <Link to="/login">
              <Button className="bg-[#E85D04] hover:bg-[#DC2F02] text-white">{t('common.doLogin')}</Button>
            </Link>
          </div>
        )}

        {/* Form */}
        {step === 'form' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl overflow-hidden">
            <div className="h-48 bg-cover bg-center relative" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1596489396342-9988b49dbbba?q=80&w=2832&auto=format&fit=crop)' }}>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20" />
              <div className="absolute bottom-0 left-0 p-6 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarDots size={28} className="text-[#FFB703]" weight="fill" />
                  <h1 className="font-['Outfit'] text-3xl font-bold">{t('dropIn.title')}</h1>
                </div>
                <p className="text-white/80">{t('dropIn.subtitleMale')}</p>
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleSubmitForm} className="space-y-5">
                <div>
                  <Label>{t('common.fullName')} *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t('common.name')} required data-testid="dropin-name" />
                </div>
                <div>
                  <Label>{t('common.phone')} *</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(000) 000-0000" required data-testid="dropin-phone" />
                </div>
                <div>
                  <Label>{t('common.email')} ({t('common.optional')})</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="voce@email.com" data-testid="dropin-email" />
                </div>
                <div>
                  <Label>{t('dropIn.preferredDate')} *</Label>
                  <Input type="date" value={formData.preferred_date} onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })} required data-testid="dropin-date" />
                </div>
                <div>
                  <Label>{t('common.notes')}</Label>
                  <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="input-field min-h-[60px] resize-none" placeholder={t('dropIn.notesPh')} data-testid="dropin-notes" />
                </div>

                {/* First-trial $15 option */}
                {eligible ? (
                  <label className="flex items-start gap-3 p-4 border-2 border-[#FFB703] bg-[#FFF3E6] rounded-xl cursor-pointer" data-testid="dropin-first-trial-label">
                    <input
                      type="checkbox"
                      checked={isFirstTrial}
                      onChange={(e) => setIsFirstTrial(e.target.checked)}
                      className="w-5 h-5 mt-0.5 accent-[#E85D04]"
                      data-testid="dropin-first-trial"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-[#E85D04] font-semibold">
                        <Sparkle size={18} weight="fill" /> {t('dropIn.firstTrialTitle')}
                      </div>
                      <p className="text-xs text-[#73706A] mt-1">
                        {t('dropIn.firstTrialDesc')} <strong className="text-[#E85D04]">$15</strong> {t('dropIn.firstTrialOnce')}
                      </p>
                    </div>
                  </label>
                ) : (
                  <div className="p-3 bg-[#F5F2EB] border border-[#E6E2D8] rounded-lg text-xs text-[#73706A]" data-testid="dropin-first-trial-used">
                    {t('dropIn.alreadyUsed')}
                  </div>
                )}

                <div className="p-4 bg-[#F5F2EB] rounded-lg text-center">
                  <p className="text-sm text-[#73706A]">{isFirstTrial ? t('dropIn.trialValue') : t('dropIn.regularValue')}</p>
                  <p className="text-3xl font-bold text-[#E85D04] font-['Outfit'] mt-1" data-testid="dropin-amount-display">${amount.toFixed(2)}</p>
                  <p className="text-xs text-[#73706A] mt-1">{t('common.card')}: ${amountCard} (+5% {t('common.tax').toLowerCase()}) | Zelle: ${amount.toFixed(2)}</p>
                </div>

                <Button type="submit" className="w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white py-3 text-base" data-testid="dropin-continue">
                  {t('dropIn.continueToPayment')}
                </Button>
              </form>
            </div>
          </div>
        )}

        {/* Redirecting to Stripe (with visible fallback link for webviews that block auto-redirect) */}
        {step === 'redirecting' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center space-y-6" data-testid="dropin-redirecting">
            <div className="w-16 h-16 rounded-full border-4 border-[#E85D04] border-t-transparent animate-spin mx-auto" />
            <div>
              <h2 className="font-['Outfit'] text-xl font-bold text-[#1A1A1A]">
                {t('dropIn.redirectingTitle') || 'Abrindo pagamento seguro...'}
              </h2>
              <p className="text-sm text-[#73706A] mt-2">
                {t('dropIn.redirectingSubtitle') || 'Se a pagina nao abrir automaticamente, toque no botao abaixo.'}
              </p>
            </div>
            {checkoutUrl && (
              <a
                href={checkoutUrl}
                target="_self"
                rel="noopener"
                className="inline-flex items-center justify-center w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white font-semibold py-4 rounded-lg text-lg transition-colors"
                data-testid="dropin-manual-checkout"
              >
                {t('dropIn.openCheckoutBtn') || 'Ir para o pagamento'} <ArrowLeft className="rotate-180 ml-2" size={20} />
              </a>
            )}
            <button onClick={() => { setStep('payment'); setLoading(false); setCheckoutUrl(null); }} className="text-sm text-[#73706A] hover:text-[#1A1A1A]">
              {t('common.back')}
            </button>
          </div>
        )}

        {/* Payment */}
        {step === 'payment' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-6 space-y-6">
            <div className="text-center">
              <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">{t('dropIn.choosePayment')}</h2>
              <p className="text-[#73706A] mt-1">{formData.name} - {formData.preferred_date}</p>
              {isFirstTrial && (
                <p className="text-xs text-[#E85D04] font-semibold mt-2 flex items-center justify-center gap-1">
                  <Sparkle size={14} weight="fill" /> {t('dropIn.firstTrialTitle')} ($15)
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <button onClick={handlePayWithCard} disabled={loading} className="p-6 border-2 border-[#E6E2D8] rounded-xl hover:border-[#E85D04] transition-all text-center group" data-testid="dropin-pay-card">
                <div className="w-16 h-16 bg-[#E85D04] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <CreditCard size={32} className="text-white" />
                </div>
                <h3 className="font-semibold text-lg text-[#1A1A1A]">{t('common.card')}</h3>
                <p className="text-xl font-bold text-[#E85D04] font-['Outfit'] mt-1">${amountCard}</p>
                <p className="text-xs text-[#73706A]">${amount.toFixed(2)} + 5%</p>
                {loading && <div className="mt-3 flex justify-center"><div className="w-5 h-5 border-2 border-[#E85D04] border-t-transparent rounded-full animate-spin" /></div>}
              </button>
              <button onClick={handlePayWithZelle} disabled={loading} className="p-6 border-2 border-[#E6E2D8] rounded-xl hover:border-[#0077B6] transition-all text-center group" data-testid="dropin-pay-zelle">
                <div className="w-16 h-16 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Phone size={32} className="text-white" />
                </div>
                <h3 className="font-semibold text-lg text-[#1A1A1A]">Zelle</h3>
                <p className="text-xl font-bold text-[#0077B6] font-['Outfit'] mt-1">${amount.toFixed(2)}</p>
                <p className="text-xs text-[#73706A]">{t('common.noFee')}</p>
              </button>
            </div>
            <button onClick={() => setStep('form')} className="text-sm text-[#73706A] hover:text-[#1A1A1A] flex items-center gap-1 mx-auto">
              <ArrowLeft size={14} /> {t('common.back')}
            </button>
          </div>
        )}

        {/* Zelle Info */}
        {step === 'zelle-info' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-6 text-center space-y-6">
            <div className="w-20 h-20 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto">
              <Phone size={40} className="text-white" />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">{t('dropIn.zelleTitle')}</h2>
              <p className="text-[#73706A] mt-2">{t('dropIn.zelleSubtitle', { amount: `$${amount.toFixed(2)}` })}</p>
            </div>
            <div className="bg-[#F5F2EB] rounded-lg p-6">
              <p className="text-sm text-[#73706A] mb-2">{t('dropIn.zelleNumber')}</p>
              <div className="flex items-center justify-center gap-3">
                <p className="text-3xl font-bold text-[#1A1A1A] font-['Outfit'] tracking-wider">{ZELLE_PHONE}</p>
                <button onClick={() => { navigator.clipboard.writeText(ZELLE_PHONE); toast.success(t('common.copied')); }} className="p-2 rounded-lg hover:bg-[#E6E2D8]">
                  <Copy size={20} className="text-[#0077B6]" />
                </button>
              </div>
              <p className="text-sm text-[#73706A] mt-3">Tia Karol Sports</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
              <p className="text-sm text-yellow-800">{t('dropIn.zelleNote')}</p>
            </div>
            <label className="cursor-pointer w-full">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={(e) => handleReceiptUpload(e.target.files[0])}
                data-testid="dropin-zelle-upload"
              />
              <Button
                className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white pointer-events-none"
                disabled={uploadingReceipt}
                data-testid="dropin-upload-btn"
              >
                {uploadingReceipt ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <UploadSimple size={20} className="mr-2" />
                )}
                {uploadingReceipt ? t('dropIn.uploadSending') : t('common.uploadReceipt')}
              </Button>
            </label>
            <Link to="/login" className="block"><Button variant="outline" className="w-full">{t('common.doLogin')}</Button></Link>
          </div>
        )}
      </main>
    </div>
  );
}
